import { Controller } from "@hotwired/stimulus"
import { renderPriceChart } from "../utils/price_chart_renderer"

// Renders an inline [chart:TICKER] embed inside a forum post.
//
// Same data endpoint and same renderer as the $TICKER popup, with the post's
// date as the as-of, so an embed shows the chart as the author saw it rather
// than as it looks today.
//
// Draws only once the embed scrolls into view: a post may carry several, and
// building a chart nobody has looked at yet costs a fetch and a canvas.
export default class extends Controller {
  static targets = ["canvas", "status", "attribution"]
  static values = {
    ticker: String,
    asOf: String,
    logScale: { type: Boolean, default: false },
    maConfig: { type: Array, default: [] },
    chartType: { type: String, default: "candle" },
    upColor: { type: String, default: "" },
    downColor: { type: String, default: "" }
  }

  connect() {
    this.handle = null
    this.disconnected = false

    this.observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return

      this.observer.disconnect()
      this.observer = null
      this.load()
    }, { rootMargin: "200px" })

    this.observer.observe(this.element)
  }

  disconnect() {
    this.disconnected = true

    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
    if (this.handle) {
      this.handle.destroy()
      this.handle = null
    }
  }

  async load() {
    let url = `/forum/chart_data?ticker=${encodeURIComponent(this.tickerValue)}`
    if (this.asOfValue) url += `&as_of=${encodeURIComponent(this.asOfValue)}`

    try {
      const response = await fetch(url, { headers: { Accept: "application/json" } })
      if (response.status === 403) {
        // A paywalled ticker (a coin, below its tier) says why in `error`.
        const body = await response.json().catch(() => ({}))
        this.setStatus(body.error || "Not available on your plan")
        return
      }
      if (!response.ok) {
        this.setStatus(response.status === 404 ? `${this.tickerValue} not found` : "Chart unavailable")
        return
      }

      const payload = await response.json()
      if (!payload.data || payload.data.length === 0) {
        this.setStatus(`No chart data for ${this.tickerValue}`)
        return
      }

      // A Turbo navigation during the fetch already ran disconnect(), when
      // this.handle was still null — so building the chart now would leak a
      // canvas and a ResizeObserver on a detached element that nothing will
      // ever tear down.
      if (this.disconnected) return

      this.setStatus("")
      this.handle = renderPriceChart(this.canvasTarget, payload.data, payload.stock, {
        logScale: this.logScaleValue,
        maConfig: this.maConfigValue,
        chartType: this.chartTypeValue,
        upColor: this.upColorValue || null,
        downColor: this.downColorValue || null
      })
      this.renderAttribution(payload.attribution)
    } catch {
      this.setStatus("Chart could not be loaded")
    }
  }

  // Licence attribution for a crypto chart, from the payload; absent for
  // the ASX. Built with textContent/href, never innerHTML.
  renderAttribution(attribution) {
    if (!this.hasAttributionTarget || !attribution) return

    const link = document.createElement("a")
    link.textContent = attribution.text
    link.href = attribution.url
    link.target = "_blank"
    link.rel = "noopener"
    this.attributionTarget.replaceChildren(link)
    this.attributionTarget.classList.remove("d-none")
  }

  setStatus(message) {
    if (!this.hasStatusTarget) return

    // textContent, not innerHTML — the ticker reaching this method came from a
    // post body.
    this.statusTarget.textContent = message
    this.statusTarget.classList.toggle("d-none", message === "")
  }
}
